import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import {
  APIKey,
  CreateAPIKeyRequest,
  CreateAPIKeyResponse,
  UpdateAPIKeyRequest,
  RevokeAPIKeyRequest,
  RotateAPIKeyRequest,
  RotateAPIKeyResponse,
  ListAPIKeysResponse,
  APIKeyFilters,
  APIKeyUsageStats
} from '../models/api-key.models';

/**
 * API Key Service
 * Handles all API key-related HTTP requests
 */
@Injectable({
  providedIn: 'root'
})
export class ApiKeyService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.apiUrl}/api-keys`;

  /**
   * Create a new API key
   * Returns the actual key (shown only once!)
   */
  createApiKey(request: CreateAPIKeyRequest): Observable<CreateAPIKeyResponse> {
    return this.http.post<CreateAPIKeyResponse>(this.API_URL, request);
  }

  /**
   * Get paginated list of API keys for the organization
   */
  getApiKeys(filters: APIKeyFilters): Observable<ListAPIKeysResponse> {
    let params = new HttpParams()
      .set('page', filters.page.toString())
      .set('page_size', filters.page_size.toString());

    if (filters.include_revoked !== undefined) {
      params = params.set('include_revoked', filters.include_revoked.toString());
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.is_active !== undefined) {
      params = params.set('is_active', filters.is_active.toString());
    }

    return this.http.get<ListAPIKeysResponse>(this.API_URL, { params });
  }

  /**
   * Get a single API key by ID
   */
  getApiKey(keyId: string): Observable<APIKey> {
    return this.http.get<APIKey>(`${this.API_URL}/${keyId}`);
  }

  /**
   * Update an existing API key
   */
  updateApiKey(keyId: string, request: UpdateAPIKeyRequest): Observable<APIKey> {
    return this.http.patch<APIKey>(`${this.API_URL}/${keyId}`, request);
  }

  /**
   * Revoke (delete) an API key
   */
  revokeApiKey(keyId: string, request?: RevokeAPIKeyRequest): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.API_URL}/${keyId}`, {
      body: request
    });
  }

  /**
   * Permanently delete an API key from the database
   * This action cannot be undone
   */
  deleteApiKey(keyId: string): Observable<{ message: string; key_id: string }> {
    return this.http.delete<{ message: string; key_id: string }>(`${this.API_URL}/${keyId}/permanent`);
  }

  /**
   * Get usage statistics for an API key
   * Note: This endpoint may need to be implemented in the backend
   */
  getApiKeyUsageStats(keyId: string): Observable<APIKeyUsageStats> {
    return this.http.get<APIKeyUsageStats>(`${this.API_URL}/${keyId}/usage`);
  }

  /**
   * Regenerate an API key (revoke old, create new with same settings)
   * Note: This is a convenience method that combines revoke + create
   */
  regenerateApiKey(keyId: string): Observable<CreateAPIKeyResponse> {
    return this.rotateApiKey(keyId, { revoke_old_key: true }).pipe(
      map(response => ({
        api_key: response.new_api_key,
        key_info: response.new_key_info
      }))
    );
  }

  /**
   * Rotate an API key (create new key with same settings, optionally revoke old key)
   * Supports grace period for gradual migration
   */
  rotateApiKey(keyId: string, request?: RotateAPIKeyRequest): Observable<RotateAPIKeyResponse> {
    return this.http.post<RotateAPIKeyResponse>(`${this.API_URL}/${keyId}/rotate`, request || {});
  }
}

